/**
 * The app's icons (FontAwesome through `IconSeeker`) under the names of the Material icons DS Repricing
 * used to import. Every other screen draws its icons with `IconSeeker`; these keep the call sites as they
 * were (`<CheckIcon fontSize="small" sx={…}/>`, `icon: CheckIcon` in a map) and change only the glyph.
 */
import React from 'react'
import { Box } from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'

// MUI's own sizes for `fontSize="small|medium|large"`
const SIZES = { small: 20, medium: 24, large: 35, inherit: 'inherit' }

// A FontAwesome glyph fills its whole font size, a Material one leaves a margin inside its box: the
// glyph is drawn at 80 % of the size asked for, so a call site keeps the visual size it had.
const glyph = (size) => (typeof size === 'number' ? Math.round(size * 0.8 * 10) / 10 : size)

// Row actions (edit, delete, copy, open) take the light weight of the app's icon buttons (`AppButtonIcon`).

// The glyph sits in a span that takes the ref, the sx and the box, as an SvgIcon would: Tooltips, Chips and
// Buttons hand their icons a ref, a className and margins, and a plain IconSeeker cannot hold a ref. The
// glyph's own size is set on the glyph, so a parent rule on the box (a Chip's icon size) cannot blow it up.
const fa = (icon, iconWeight) => React.forwardRef(function Icon(
	{ fontSize = 'medium', sx = {}, style, className, titleAccess, color }, ref,
) {
	const size = sx.fontSize ?? SIZES[fontSize] ?? fontSize
	return <Box component="span" ref={ref} className={className} style={style} aria-label={titleAccess}
	            sx={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
	                  lineHeight: 1,
	                  ...(color && color !== 'inherit' && color !== 'action' ? { color: `${color}.main` } : {}),
	                  ...sx, fontSize: size }}>
		<IconSeeker icon={icon} {...(iconWeight ? { iconWeight } : {})}
		            sx={{ fontSize: glyph(size), color: 'inherit' }}/>
	</Box>
})

export const Add = fa('plus')
export const AddBoxOutlined = fa('square-plus')
export const AddCircleOutline = fa('circle-plus')
export const ArrowRightAlt = fa('arrow-right')
export const ArrowRightAltOutlined = fa('arrow-right')
export const AutoAwesome = fa('sparkles')
export const AutoFixHighOutlined = fa('wand-magic-sparkles')
export const BlockOutlined = fa('ban')
export const Bolt = fa('bolt')
export const CalendarMonth = fa('calendar-days')
export const CancelOutlined = fa('circle-xmark')
export const CheckBox = fa('square-check')
export const CheckBoxOutlineBlank = fa('square', 'regular')
export const CheckCircle = fa('circle-check')
export const CheckCircleOutline = fa('circle-check')
export const ChevronLeft = fa('chevron-left')
export const ChevronRight = fa('chevron-right')
export const Close = fa('xmark')
export const CloseOutlined = fa('xmark')
export const ContentCopy = fa('clone', 'light')
export const ContentCopyOutlined = fa('clone', 'light')
export const DeleteOutline = fa('trash-can', 'light')
export const Done = fa('check')
export const DownloadOutlined = fa('download')
export const DragIndicator = fa('grip-dots-vertical')
export const Edit = fa('pen', 'light')
export const EditOutlined = fa('pen', 'light')
export const EmojiEvents = fa('trophy')
export const ErrorOutline = fa('circle-exclamation')
export const Event = fa('calendar-day')
export const ExpandLess = fa('chevron-up')
export const ExpandMore = fa('chevron-down')
export const FiberManualRecord = fa('circle')
export const FilterAltOutlined = fa('filter')
export const FlightLand = fa('plane-arrival')
export const FlightTakeoff = fa('plane-departure')
export const HorizontalRule = fa('minus')
export const InboxOutlined = fa('inbox')
export const InfoOutlined = fa('circle-info')
export const Layers = fa('layer-group')
export const LayersOutlined = fa('layer-group')
export const LinkOffOutlined = fa('link-slash')
export const LinkOutlined = fa('link')
export const ManageSearchOutlined = fa('magnifying-glass-arrow-right')
export const MoreHorizOutlined = fa('ellipsis')
export const OpenInNew = fa('up-right-from-square', 'light')
export const OpenInNewOutlined = fa('up-right-from-square', 'light')
export const PauseCircleOutline = fa('circle-pause')
export const PlayArrow = fa('play')
export const PlayCircleOutline = fa('circle-play')
export const PublishOutlined = fa('upload')
export const RadioButtonUnchecked = fa('circle', 'regular')
export const Refresh = fa('rotate-right')
export const RefreshOutlined = fa('rotate-right')
export const RuleOutlined = fa('list-check')
export const Schedule = fa('clock')
export const ScienceOutlined = fa('flask')
export const SearchOff = fa('magnifying-glass-minus')
export const SearchOutlined = fa('magnifying-glass')
export const ShieldOutlined = fa('shield-halved')
export const Store = fa('store')
export const SubdirectoryArrowRight = fa('arrow-turn-down-right')
export const TuneOutlined = fa('sliders')
export const UploadFileOutlined = fa('file-arrow-up')
export const VerticalAlignBottomOutlined = fa('arrow-down-to-line')
export const WarningAmber = fa('triangle-exclamation')
export const WarningAmberOutlined = fa('triangle-exclamation')
export const WarningAmberRounded = fa('triangle-exclamation')
